/*
  TMA — add to cart without leaving the page.

  The add-to-cart controls are real <form action="/cart/add"> elements. Left
  alone, the browser posts them and Shopify answers with a redirect to /cart,
  which is why every add threw the shopper into the cart. That fights the whole
  pricing mechanic: the discount needs three mixes in the basket, so the one
  thing the page should encourage is adding another.

  This intercepts the submit and posts to /cart/add.js instead, then updates the
  header count and raises a toast. PROGRESSIVE ENHANCEMENT: if fetch is missing,
  or the request fails for any reason, the form is submitted the ordinary way
  and the shopper still lands on a working cart page. Nothing here is required
  for the store to function.
*/
(function () {
  'use strict';

  if (!window.fetch || !window.FormData) return;

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  /* ---- the toast ------------------------------------------------------ */

  var toastEl = null;
  var toastTimer = null;

  function toastHost() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'tma-toast';
    /* polite: announced after whatever the shopper is already reading, and it
       never steals focus, so keyboard position is preserved. */
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function showToast(title, cartUrl) {
    var host = toastHost();
    host.innerHTML = '';

    var card = document.createElement('div');
    card.className = 'tma-toast-card';

    var tick = document.createElement('span');
    tick.className = 'tma-toast-tick';
    tick.setAttribute('aria-hidden', 'true');
    tick.innerHTML =
      '<svg viewBox="0 0 24 24"><path d="M4 12.5l5.2 5.2L20 7"/></svg>';

    var text = document.createElement('p');
    text.className = 'tma-toast-text';
    text.textContent = title + ' added to your cart';

    var link = document.createElement('a');
    link.className = 'tma-toast-link';
    link.href = cartUrl || '/cart';
    link.textContent = 'View cart';

    card.appendChild(tick);
    card.appendChild(text);
    card.appendChild(link);
    host.appendChild(card);

    /* Reflow, then flip the class, so the entry transition runs from the
       hidden position. Not requestAnimationFrame: it is throttled in
       background tabs and the toast would never appear. */
    void host.offsetWidth;
    host.classList.add('is-on');

    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { host.classList.remove('is-on'); }, 4500);
  }

  /* ---- the header count ----------------------------------------------- */

  /* Optimistic move, reconciled by updateCount when the server answers. */
  function bumpCountBy(n) {
    var badge = document.querySelector('.hdr .bag b');
    if (!badge) return;
    var current = parseInt(badge.textContent, 10);
    if (isNaN(current)) return;
    updateCount(current + n);
  }

  function updateCount(count) {
    var bag = document.querySelector('.hdr .bag');
    if (!bag) return;

    /* the 2026 header shows the count at all times, zero included, so the
       badge is never removed — it is part of the bag lockup, not a flag */
    var badge = bag.querySelector('b');
    if (!badge) {
      badge = document.createElement('b');
      badge.setAttribute('aria-hidden', 'true');
      bag.appendChild(badge);
    }
    badge.textContent = count;

    /* the accessible name carries the count, so screen reader users get the
       same information the badge gives everyone else */
    var label = bag.getAttribute('aria-label') || 'Cart';
    bag.setAttribute('aria-label', label.replace(/,\s*\d+\s*$/, '') + ', ' + count);

    if (reduceMotion) return;
    bag.classList.remove('is-bumped');
    void bag.offsetWidth;               /* restart the animation */
    bag.classList.add('is-bumped');
  }

  /* ---- submit handling ------------------------------------------------ */

  function isAddForm(form) {
    var action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') !== -1;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !isAddForm(form)) return;

    var button = form.querySelector('[name="add"]');
    event.preventDefault();

    /* innerHTML, not textContent: the button holds two spans and the gap
       between them comes from flex. Restoring textContent flattened them into
       one text node, so after the first add the label read "ADD+" with the
       space gone. */
    var original = button ? button.innerHTML : '';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
    }

    /* Move the badge now rather than after two sequential round trips
       (/cart/add.js, then /cart.js for the true total). On the live store that
       wait is over a second, which reads as the button doing nothing. The real
       count overwrites this the moment it lands, and a failed add falls back
       to a full form POST, which repaints the header from the server anyway. */
    var qtyField = form.querySelector('[name="quantity"]');
    var added = qtyField ? parseInt(qtyField.value, 10) : 1;
    if (!added || added < 1) added = 1;
    bumpCountBy(added);

    function restore() {
      if (!button) return;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = original;
    }

    /* If anything at all goes wrong, hand the job back to the browser. */
    function fallback() {
      restore();
      form.submit();
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(function (item) {
        return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            showToast(item.product_title || item.title || 'Item', '/cart');
            restore();
            /* The listener below owns the badge, so every path that changes the
               cart repaints it the same way. Eurus drawers listen too. */
            document.dispatchEvent(new CustomEvent('tma:cart:updated', { detail: cart }));
          });
      })
      .catch(fallback);
  });

  /* The cart page renders its line items server-side. Its own quantity
     controls post the form and reload, so they stay in step -- but the upsell
     row below them changes the cart over fetch, which left the lines above it
     stale until a manual refresh. Reloading here is what the cart's own
     controls already do, so the two behave the same way.

     Scoped to the cart page: everywhere else the count and the toast are the
     right feedback and a reload would be hostile. */
  document.addEventListener('tma:cart:updated', function (event) {
    /* Every cart change lands here, whoever made it. The card steppers were
       the gap: tma-card-qty.js repaints the steppers from the server but never
       touched the header, so once a product was in the cart -- which is when a
       card stops showing Add and starts showing +/- -- the badge stopped
       moving and went stale against the real cart. */
    var cart = event && event.detail;
    if (cart && typeof cart.item_count === 'number') updateCount(cart.item_count);

    if (!/\/cart(\/|$)/.test(window.location.pathname)) return;
    window.location.reload();
  });
})();
